import { Button, FormControl } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { Formik } from "formik";
import * as Yup from "yup";

import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import GeneralValidationError from "@/common/components/Error/GeneralValidationError";
import FormActions from "@/common/components/Form/FormActions";
import AppIcon from "@/common/components/Icons/AppIcon";
import { FormikHelper } from "@/common/helpers/formik";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import {
  GeneralBillToInfoInputDto,
  GeneralBillToInfoOfEntityUpdateDto,
} from "@/core/api/generated/v1";

import {
  GeneralBillToInfoInput,
  prepareGeneralBillToInfoInputDtoForApi,
} from "./GeneralBillToInfoInput";

export interface GeneralBillToInfoOfEntityUpdateProps<TResult> {
  initialValue: Nil<GeneralBillToInfoInputDto>;
  saveFunc: (newValue: GeneralBillToInfoOfEntityUpdateDto | undefined) => Promise<TResult | null>;
  onSave: (newValue: TResult | null) => void;
}

export default function GeneralBillToInfoOfEntityUpdate<TResult>({
  initialValue,
  saveFunc,
  onSave,
}: GeneralBillToInfoOfEntityUpdateProps<TResult>) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();

  return (
    <Formik<GeneralBillToInfoOfEntityUpdateDto & BaseFormikValues>
      enableReinitialize={false}
      initialValues={{
        billTo: initialValue ? { ...initialValue } : undefined,
        submit: "",
      }}
      validationSchema={Yup.object().shape({
        // name: Yup.string().required("Name is required"),
      })}
      onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
        try {
          const result = await saveFunc({
            ...values,
            billTo: prepareGeneralBillToInfoInputDtoForApi(values.billTo) || undefined,
          });
          onSave(result);
          enqueueSnackbar("Bill to info saved.", { variant: "success" });

          if (mounted.current) {
            setStatus({ success: true });
            setSubmitting(false);
          }
        } catch (err: any) {
          if (mounted.current) {
            ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
            setStatus({ success: false });
            setSubmitting(false);
          }
        }
      }}
    >
      {(formikProps) => {
        const { errors, handleSubmit, isSubmitting, values, setFieldValue } = formikProps;

        return (
          <form noValidate onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <Box>
                {!values.billTo && (
                  <Box>
                    <NoDataAlert fullWidth={false} />

                    <Stack
                      direction='row'
                      sx={{
                        height: "100%",
                        alignItems: "center",
                        justifyContent: { lg: "flex-end" },
                      }}
                    >
                      <Button
                        variant='text'
                        color='text'
                        size='small'
                        startIcon={<AppIcon of='add' />}
                        onClick={() => {
                          setFieldValue("billTo", {
                            ...initialValue,
                          });
                        }}
                      >
                        Add bill to
                      </Button>
                    </Stack>
                  </Box>
                )}

                {values.billTo && (
                  <Stack spacing={1}>
                    <FormControl fullWidth margin='dense'>
                      <GeneralBillToInfoInput
                        formikProps={FormikHelper.getSubProps(
                          formikProps,
                          "billTo",
                          (x) => x.billTo,
                        )}
                        inferFromEntities={undefined}
                      />
                    </FormControl>

                    <Stack
                      direction='row'
                      sx={{
                        height: "100%",
                        alignItems: "center",
                        justifyContent: { lg: "flex-end" },
                      }}
                    >
                      <Button
                        variant='text'
                        color='text'
                        size='small'
                        startIcon={<AppIcon of='remove' />}
                        onClick={() => {
                          setFieldValue("billTo", undefined);
                        }}
                      >
                        Remove bill to info
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Box>

              <GeneralValidationError sx={{ my: 1 }} errors={errors} />

              <FormActions>
                <Button
                  color='primary'
                  loading={isSubmitting}
                  fullWidth
                  type='submit'
                  variant='contained'
                >
                  Save
                </Button>
              </FormActions>
            </Stack>
          </form>
        );
      }}
    </Formik>
  );
}
